import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import {
  AppBar,
  Box,
  MenuItem,
  Select,
  Toolbar,
  Typography
} from '@mui/material';
import { KeyboardArrowDown, NorthEast } from '@mui/icons-material';
import { Button } from 'system/atoms';
import { OrganizationGroupRole } from 'generated-graphql/types';
import { PathsV2 } from 'core/services/navigation/pathsV2';
import { useUser } from 'core/stores/hooks/useUser';
import { organizationAtom } from 'services/organization/atom';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';
import { Paths } from 'services/navigation/paths';
import { useLoadOrganizationIntegration } from 'services/integration/hooks';
import { useUserFetcher } from 'services/user/hooks';
import { MainLogo } from 'common/ui/MainLogo';
import { navbarSx } from 'modules/layout/styles';
import {
  buildEmbeddedWorkflowsUrl,
  embeddedCandidatesPath
} from '../constants/atsEmbedLinks';
import { getAtsEmbedContext } from '../utils/atsEmbedContext';
import { getEmbeddedJobId } from '../utils/embeddedJobId';
import { navigateFrameTo } from '../utils/navigateFrameTo';
import { atsEmbedSx } from '../styles';

export const EmbeddedNavBar: React.FC = () => {
  const user = useUser();
  const organization = useRecoilValue(organizationAtom);
  const organizations = useRecoilValue(organizationGroupOrganizationsAtom);
  const { changeOrganization } = useUserFetcher();
  useLoadOrganizationIntegration();
  const navigate = useNavigate();
  const location = useLocation();
  const isOrganizationGroupAdmin =
    user.organizationGroupRole === OrganizationGroupRole.OrganizationGroupAdmin;
  const isCandidatesActive = location.pathname.startsWith(
    PathsV2.CANDIDATE_PROFILE_LIST
  );
  const embedContext = getAtsEmbedContext();

  const handleOrganizationChange = (organizationId: string) => {
    const chosenOrganization = organizations?.find(
      (candidateOrganization) => candidateOrganization.id === organizationId
    );
    if (!chosenOrganization) return;
    changeOrganization(user.id, chosenOrganization, user.organizationGroupRole);
  };

  return (
    <AppBar position="sticky" component="nav" elevation={0}>
      <Toolbar sx={navbarSx.toolbar} disableGutters>
        <Box sx={atsEmbedSx.navbarStart}>
          <MainLogo />
          <Typography sx={atsEmbedSx.separator} aria-hidden>
            /
          </Typography>
          <Select
            variant="standard"
            sx={atsEmbedSx.subAccountSelect}
            value={organization?.id ?? ''}
            IconComponent={KeyboardArrowDown}
            inputProps={{ 'aria-label': 'Sub-account' }}
            onChange={(event) => handleOrganizationChange(event.target.value)}
          >
            {(organizations ?? []).map((candidateOrganization) => (
              <MenuItem
                key={candidateOrganization.id}
                value={candidateOrganization.id}
              >
                {candidateOrganization.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={atsEmbedSx.navLinks}>
          <Button
            aria-pressed={false}
            variant="tertiary"
            sx={atsEmbedSx.navLinkButton}
            disabled={!organization || !embedContext}
            onClick={() =>
              organization &&
              embedContext &&
              navigateFrameTo(
                buildEmbeddedWorkflowsUrl({
                  organizationId: organization.id,
                  instanceId: embedContext.instanceId,
                  jobId: getEmbeddedJobId(embedContext, organization.id)
                })
              )
            }
          >
            Workflows
          </Button>
          <Button
            aria-pressed={isCandidatesActive}
            variant="tertiary"
            sx={
              isCandidatesActive
                ? atsEmbedSx.selectedNavLinkButton
                : atsEmbedSx.navLinkButton
            }
            onClick={() => navigate(embeddedCandidatesPath)}
          >
            Candidates
          </Button>
          {isOrganizationGroupAdmin && (
            <Button
              variant="tertiary"
              sx={atsEmbedSx.navLinkButton}
              endIcon={<NorthEast fontSize="small" />}
              onClick={() =>
                window.open(Paths.ADMINISTRATOR, '_blank', 'noopener')
              }
            >
              Admin
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export const withEmbeddedNavbar = (element: React.ReactElement) => (
  <>
    <EmbeddedNavBar />
    {element}
  </>
);
